import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
    Button, TextField, IconButton, Autocomplete, Snackbar, Alert, useTheme, useMediaQuery
} from '@mui/material';
import BurbujasLoader from '../UIElements/BurbujasLoader';
import { Close as CloseIcon } from '@mui/icons-material';
import { getCursos, quickCreateCurso } from '../../services/cursos.service.js';
import { createCcAsistenciaEvento, updateCcAsistenciaEvento } from '../../services/cc_asistencia.service.js';
import { Add as AddIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

const parseTimeToDayjs = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.trim().split(':');
    if (parts.length >= 2) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
            return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
        }
    }
    return null;
};

export default function ModalCrearCcEvento({ open, onClose, onSuccess, initialData }) {
    const isEditMode = !!initialData;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [cursos, setCursos] = useState([]);
    const [selectedCurso, setSelectedCurso] = useState(null);
    const [fecha, setFecha] = useState('');
    const [horarioInicio, setHorarioInicio] = useState('');
    const [horarioFin, setHorarioFin] = useState('');
    const [docente, setDocente] = useState('');
    const [ubicacion, setUbicacion] = useState('');
    const [cupo, setCupo] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    // States for Quick Course Creation
    const [openQuickCreate, setOpenQuickCreate] = useState(false);
    const [quickCourseName, setQuickCourseName] = useState('');
    const [quickCourseHours, setQuickCourseHours] = useState('');
    const [loadingQuickCreate, setLoadingQuickCreate] = useState(false);

    const showAlert = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    useEffect(() => {
        if (open) {
            loadCursos();
            if (isEditMode) {
                setFecha(initialData.fecha || '');
                setHorarioInicio(initialData.horario_inicio ? initialData.horario_inicio.substring(0, 5) : '');
                setHorarioFin(initialData.horario_fin ? initialData.horario_fin.substring(0, 5) : '');
                setDocente(initialData.docente || '');
                setUbicacion(initialData.ubicacion || '');
                setCupo(initialData.cupo !== undefined && initialData.cupo !== null ? initialData.cupo : '');
            } else {
                setFecha(''); setHorarioInicio(''); setHorarioFin(''); setDocente(''); setUbicacion(''); setCupo(''); setSelectedCurso(null);
            }
        }
    }, [open, isEditMode, initialData]);

    const loadCursos = async () => {
        try {
            const data = await getCursos();
            setCursos(data);
            if (isEditMode && initialData && initialData.curso_cod) {
                const found = data.find(c => c.cod === initialData.curso_cod);
                if (found) setSelectedCurso(found);
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
        }
    };

    const handleQuickCreate = async () => {
        if (!quickCourseName || quickCourseName.trim() === '' || !quickCourseHours) {
            showAlert("Nombre y cantidad de horas son obligatorios.");
            return;
        }

        setLoadingQuickCreate(true);
        try {
            const newCurso = await quickCreateCurso(quickCourseName, parseInt(quickCourseHours));
            await loadCursos();
            setSelectedCurso(newCurso);
            setOpenQuickCreate(false);
            setQuickCourseName('');
            setQuickCourseHours('');
            showAlert("Curso creado correctamente.", "success");
        } catch (error) {
            console.error('Error in quick create:', error);
            showAlert(error.message || "Error al crear el curso.");
        } finally {
            setLoadingQuickCreate(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!isEditMode && !selectedCurso) {
            showAlert("Debe seleccionar un curso.");
            return;
        }
        if (!fecha) {
            showAlert("La fecha del evento es obligatoria.");
            return;
        }
        if (!horarioInicio) {
            showAlert("El horario de inicio es obligatorio.");
            return;
        }
        if (!horarioFin) {
            showAlert("El horario de fin es obligatorio.");
            return;
        }
        if (horarioInicio >= horarioFin) {
            showAlert("El horario de inicio debe ser anterior al horario de fin.");
            return;
        }
        if (!ubicacion || ubicacion.trim() === '') {
            showAlert("La ubicación es obligatoria.");
            return;
        }
        if (!docente || docente.trim() === '') {
            showAlert("El nombre del docente es obligatorio.");
            return;
        }
        const cupoStr = cupo !== null && cupo !== undefined ? cupo.toString().trim() : '';
        if (cupoStr === '') {
            showAlert("El cupo es obligatorio.");
            return;
        }
        const cupoParsed = parseInt(cupoStr, 10);
        if (!/^\d+$/.test(cupoStr) || isNaN(cupoParsed) || cupoParsed < 1) {
            showAlert("El cupo debe ser un número entero mayor o igual a 1.");
            return;
        }

        setLoading(true);
        const requestData = {
            curso_cod: selectedCurso ? selectedCurso.cod : initialData?.curso_cod,
            fecha,
            horario_inicio: horarioInicio,
            horario_fin: horarioFin,
            docente,
            ubicacion,
            cupo: cupoParsed
        };

        try {
            if (isEditMode) {
                await updateCcAsistenciaEvento(initialData.id, requestData);
                onSuccess(`Evento actualizado con éxito`);
            } else {
                await createCcAsistenciaEvento(requestData);
                onSuccess(`Nuevo evento creado con éxito`);
            }
            onClose();
        } catch (error) {
            console.error('Error saving event:', error);
            showAlert(`Error al guardar: ${error.message || 'Error desconocido'}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <form onSubmit={handleSave}>
                <DialogTitle sx={{ m: 0, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A' }}>
                        {isEditMode ? 'Editar Evento' : 'Crear Evento'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: '#9E9E9E', '&:hover': { color: '#1A1A1A', bgcolor: 'rgba(0,0,0,0.04)' } }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 4, pt: 1 }}>
                    <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                Curso / Capacitación *
                            </Typography>
                            <Autocomplete
                                options={cursos}
                                getOptionLabel={(option) => `${option.nombre}`}
                                value={selectedCurso}
                                onChange={(event, newValue) => setSelectedCurso(newValue)}
                                disabled={isEditMode}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Seleccione un curso"
                                        required={!isEditMode}
                                        InputProps={{ ...params.InputProps, sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                                    />
                                )}
                            />
                            {!isEditMode && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setOpenQuickCreate(true)}
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            color: 'primary.main',
                                            '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' }
                                        }}
                                    >
                                        CREAR CURSO
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 3, flexDirection: isMobile ? 'column' : 'row' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                    Fecha del Evento *
                                </Typography>
                                <TextField
                                    type="date"
                                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                    required
                                    fullWidth
                                    value={fecha}
                                    onChange={(e) => setFecha(e.target.value)}
                                    disabled={isEditMode}
                                    InputProps={{ sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                    Horario Inicio *
                                </Typography>
                                <TimePicker
                                    ampm={false}
                                    format="HH:mm"
                                    disabled={isEditMode}
                                    value={parseTimeToDayjs(horarioInicio)}
                                    onChange={(newValue) => {
                                        if (!newValue) {
                                            setHorarioInicio('');
                                        } else if (newValue.isValid && newValue.isValid()) {
                                            setHorarioInicio(newValue.format('HH:mm'));
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            placeholder: "HH:mm",
                                            InputProps: { sx: { fontFamily: 'Poppins', borderRadius: '8px' } },
                                            sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px' } }
                                        }
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                    Horario Fin *
                                </Typography>
                                <TimePicker
                                    ampm={false}
                                    format="HH:mm"
                                    disabled={isEditMode}
                                    value={parseTimeToDayjs(horarioFin)}
                                    onChange={(newValue) => {
                                        if (!newValue) {
                                            setHorarioFin('');
                                        } else if (newValue.isValid && newValue.isValid()) {
                                            setHorarioFin(newValue.format('HH:mm'));
                                        }
                                    }}
                                    minTime={parseTimeToDayjs(horarioInicio) || undefined}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            placeholder: "HH:mm",
                                            InputProps: { sx: { fontFamily: 'Poppins', borderRadius: '8px' } },
                                            sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px' } }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                Ubicación *
                            </Typography>
                            <TextField
                                placeholder="Ej. 27 de abril 123"
                                fullWidth
                                required
                                value={ubicacion}
                                onChange={(e) => setUbicacion(e.target.value)}
                                InputProps={{ sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                Nombre y Apellido del Docente *
                            </Typography>
                            <TextField
                                placeholder="Ingrese el nombre del docente"
                                fullWidth
                                required
                                value={docente}
                                onChange={(e) => setDocente(e.target.value)}
                                InputProps={{ sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                Cupo Máximo *
                            </Typography>
                                <TextField
                                    type="number"
                                    fullWidth
                                    required
                                    inputProps={{ min: "1", step: "1" }}
                                    value={cupo}
                                    onChange={(e) => setCupo(e.target.value)}
                                    InputProps={{ sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                                />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 4, pt: 2, gap: 2 }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            fontFamily: 'Geogrotesque Sharp',
                            color: '#64748B',
                            fontSize: '1rem',
                            height: '48px',
                            px: 3,
                            '&:hover': { bgcolor: '#F1F5F9' }
                        }}
                    >
                        CANCELAR
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            fontFamily: 'Geogrotesque Sharp',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            height: '48px',
                            px: 4,
                            boxShadow: '0 4px 12px rgba(0,123,255,0.2)'
                        }}
                    >
                        {loading ? <BurbujasLoader small /> : (isEditMode ? 'GUARDAR' : 'CREAR')}
                    </Button>
                </DialogActions>
            </form>
        </LocalizationProvider>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: '12px' }} variant="filled">{snackbar.message}</Alert>
            </Snackbar>

            {/* Modal de Creación Rápida de Curso */}
            <Dialog
                open={openQuickCreate}
                onClose={() => !loadingQuickCreate && setOpenQuickCreate(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold' }}>
                    Creación rápida de curso
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                Nombre del curso *
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Ej. Taller de Oratoria"
                                value={quickCourseName}
                                onChange={(e) => setQuickCourseName(e.target.value)}
                                InputProps={{ sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins', mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                                Duración / Cantidad de horas *
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                placeholder="Ej. 10"
                                value={quickCourseHours}
                                onChange={(e) => setQuickCourseHours(e.target.value)}
                                InputProps={{ sx: { fontFamily: 'Poppins', borderRadius: '8px' } }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={() => setOpenQuickCreate(false)}
                        disabled={loadingQuickCreate}
                        sx={{ fontFamily: 'Geogrotesque Sharp', color: '#64748B' }}
                    >
                        CANCELAR
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleQuickCreate}
                        disabled={loadingQuickCreate}
                        sx={{ fontFamily: 'Geogrotesque Sharp', borderRadius: '8px', px: 3 }}
                    >
                        {loadingQuickCreate ? <BurbujasLoader small /> : 'CREAR CURSO'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
